/**
 * Offline Game Plan Data Hooks
 * 
 * Hooks for accessing game plan and template data with offline support.
 * Handles both user-specific game plans and shared templates.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useOfflineState } from '../useOfflineState';
import { indexedDBService } from '@/services/offline/indexeddb';
import type { GamePlanOffline } from '@/services/offline/indexeddb/schema';
import type { DataFetchOptions, DataFetchResult } from './shared';

/**
 * Hook for user game plans with offline support
 * 
 * Fetches user's personal game plans with cache-first loading.
 * Handles user-specific game plan data with proper filtering.
 * 
 * @param userId - The user ID to fetch game plans for
 * @param options - Fetch options including cache behavior
 * @returns User game plans data with loading states and refresh capability
 */
export function useOfflineGamePlans(
  userId?: string,
  options: DataFetchOptions = {}
): DataFetchResult<GamePlanOffline[]> {
  const { state } = useOfflineState();
  const [data, setData] = useState<GamePlanOffline[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Use ref to track current data without causing re-renders
  const dataRef = useRef<GamePlanOffline[] | null>(null);
  dataRef.current = data;

  const { forceRefresh = false } = options;

  const fetchData = useCallback(async () => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Always try cache first for offline-first approach
      const cachedData = await indexedDBService.gamePlans.getByUserId(userId);

      if (cachedData.length > 0) {
        setData(cachedData);
        setIsFromCache(true);
        setLastUpdated(new Date().toISOString());
        setLoading(false);

        // If offline or not forcing refresh, use cached data and stop here
        if (!state.isOnline || !forceRefresh) {
          return;
        }

        // If online and forcing refresh, continue to sync but we already have data
      } else if (!state.isOnline) {
        // If offline and no cache, return empty array
        setData([]);
        setLoading(false);
        return;
      }

      // Only sync if:
      // 1. We're online AND
      // 2. We're forcing refresh (user explicitly requested it)
      // Note: Don't sync just because user has no game plans - they might legitimately have none
      const shouldSync = state.isOnline && forceRefresh;
      if (shouldSync) {
        try {
          // Import sync service dynamically to avoid circular dependencies
          const { syncService } = await import('@/services/offline');

          // Only sync if not already syncing to prevent loops
          if (!syncService.getStatus().isSyncing) {
            await syncService.performSync();
          }
          const updatedData = await indexedDBService.gamePlans.getByUserId(userId);
          setData(updatedData);
          setIsFromCache(false);
        } catch (syncError) {
          console.error('Sync failed:', syncError);
          // Continue with cached data if available
          if (cachedData.length === 0) {
            setData([]);
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch game plans';
      setError(errorMessage);

      // Try to get cached data as fallback
      try {
        const cachedData = await indexedDBService.gamePlans.getByUserId(userId);
        setData(cachedData);
        setIsFromCache(true);
      } catch {
        setData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, state.isOnline, forceRefresh]);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}

/**
 * Hook for game plan templates with offline support
 *
 * Fetches shared game plan templates with cache-first loading.
 * Templates are shared resources that can be used as starting points for new game plans.
 *
 * @param _userId - Optional user ID for user-specific templates (unused but kept for API consistency)
 * @param _options - Fetch options (currently unused but maintained for consistency)
 * @returns Game plan templates data with loading states and refresh capability
 */
export function useOfflineGamePlanTemplates(
  _userId?: string,
  _options: DataFetchOptions = {}
): DataFetchResult<GamePlanOffline[]> {
  const [data, setData] = useState<GamePlanOffline[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // For templates, we primarily use cached data
      // Templates are relatively static and don't change frequently
      const cachedData = await indexedDBService.gamePlans.getAllTemplates();
      const adminTemplates = await indexedDBService.gamePlans.getAdminTemplates();

      // If no admin templates found, try to sync them
      if (adminTemplates.length === 0 && navigator.onLine) {
        try {
          const { gamePlanDataLoader } = await import('@/services/offline/sync/loaders/GamePlanDataLoader');
          const syncResult = await gamePlanDataLoader.loadAdminTemplates();

          if (syncResult.count > 0) {
            // Reload templates after sync
            const updatedData = await indexedDBService.gamePlans.getAllTemplates();
            setData(updatedData);
            setIsFromCache(false);
            setLastUpdated(new Date().toISOString());
            return; // Exit early since we have updated data
          }
        } catch (syncError) {
          console.error('Failed to sync admin templates:', syncError);
        }
      }

      // Set cached data if no sync occurred or sync failed
      setData(cachedData);
      setIsFromCache(true);
      setLastUpdated(new Date().toISOString());
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch game plan templates';
      setError(errorMessage);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    isFromCache,
    lastUpdated,
    refresh
  };
}
